import { useState, useEffect, useCallback } from 'react';
import { FileText, Archive, Loader2, Files, Mail, X, Paperclip, History, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  AGENCIES, DEFAULT_AGENCY,
  loadPdfLibs, functiegroepRates, entryToWorker, workerHasHours, generateWorkerPdf, generateTotalListPdf,
} from './urenlijstPdf';
import './WeekGenerate.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

function safeFileName(name) {
  return String(name || '').replace(/[\\/*?:"<>|]/g, '').trim();
}

// `lijstId` = de urenlijst (meerdere per week mogelijk); `label` = de
// klant, zodat bestanden van twee lijsten in dezelfde week verschillen.
export default function WeekGenerate({ profiel, jaar, week, lijstId, label = '' }) {
  const labelSuffix = label ? ` ${safeFileName(label)}` : '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatable, setGeneratable] = useState([]); // [{ id, worker, rates }]
  const [selectedId, setSelectedId] = useState('');
  const [generating, setGenerating] = useState(false);

  // Mail dialog state. mode = 'person' | 'company' | null
  const [mail, setMail] = useState(null); // { mode, to, subject, item? }
  const [mailSending, setMailSending] = useState(false);
  const [mailNote, setMailNote] = useState(null); // { ok, text }

  // Mail history modal. null = closed; otherwise { loading, scope: 'week'|'all', entries }
  const [history, setHistory] = useState(null);

  const agency = AGENCIES[profiel] || DEFAULT_AGENCY;

  const pdfToBase64 = (doc) => doc.output('datauristring').split(',')[1];

  // Single source of truth for what a worker's mail looks like — used by both the
  // preview modal and the actual send, so the preview is exactly what goes out.
  const buildSubject = (name) => `Urenlijst week ${week} — ${name}`;
  const buildBody = (name) =>
    `Beste ${name},\n\nIn de bijlage je urenlijst van week ${week} (${jaar}).\n\nMet vriendelijke groet,\n${agency.name}`;
  const attachmentName = (name) => `${safeFileName(name)} uren wk ${week}.pdf`;

  // Fetch workers + this week's entries + functiegroepen and build the generatable list.
  const fetchGeneratable = useCallback(async () => {
    const [workersRes, weekRes, fgRes] = await Promise.all([
      authFetch('/api/workers').then((r) => { if (!r.ok) throw new Error('workers'); return r.json(); }),
      authFetch(`/api/uren/week?lijst_id=${encodeURIComponent(lijstId)}`)
        .then((r) => { if (!r.ok) throw new Error('week'); return r.json(); }),
      authFetch('/api/zzp/functiegroepen').then((r) => { if (!r.ok) throw new Error('functiegroepen'); return r.json(); }),
    ]);
    const workersArr = Array.isArray(workersRes) ? workersRes : [];
    const entries = Array.isArray(weekRes?.entries) ? weekRes.entries : [];
    const fgArr = Array.isArray(fgRes) ? fgRes : [];
    const metaById = new Map(workersArr.map((w) => [w.id, w]));
    const fgById = new Map(fgArr.map((f) => [f.id, f]));

    const list = [];
    for (const entry of entries) {
      const meta = metaById.get(entry.worker_id);
      if (!meta) continue;
      const worker = entryToWorker(entry, meta);
      if (!workerHasHours(worker)) continue;
      const fg = meta.functiegroepId ? fgById.get(meta.functiegroepId) : null;
      list.push({ id: meta.id, worker, rates: functiegroepRates(fg), email: meta.email || '' });
    }
    list.sort((a, b) => String(a.worker.name || '').localeCompare(String(b.worker.name || ''), 'nl', { sensitivity: 'base' }));
    return list;
  }, [profiel, jaar, week, lijstId]);

  // Initial + prop-change load (for the count and the person picker).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setGeneratable([]);
    setSelectedId('');
    fetchGeneratable()
      .then((list) => {
        if (cancelled) return;
        setGeneratable(list);
        if (list.length) setSelectedId(String(list[0].id));
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError('Kon gegevens niet laden.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [fetchGeneratable]);

  // Flush the grid's pending autosave, then re-fetch the freshest data so exports
  // always reflect just-typed hours (no page refresh needed).
  const getFresh = useCallback(async () => {
    window.dispatchEvent(new CustomEvent('uren:flush'));
    await new Promise((r) => setTimeout(r, 450)); // let the flushed PUTs land
    const list = await fetchGeneratable();
    setGeneratable(list);
    setSelectedId((prev) => (list.some((g) => String(g.id) === String(prev)) ? prev : (list[0] ? String(list[0].id) : '')));
    return list;
  }, [fetchGeneratable]);

  const handleGenerateOne = useCallback(async () => {
    setError('');
    setGenerating(true);
    try {
      await loadPdfLibs();
      const list = await getFresh();
      const item = list.find((g) => String(g.id) === String(selectedId)) || list[0];
      if (!item) { setError('Deze medewerker heeft geen uren.'); return; }
      const doc = generateWorkerPdf(item.worker, { weekNr: week, clientName: '', agency, rates: item.rates });
      doc.save(`${safeFileName(item.worker.name)} uren wk ${week}.pdf`);
    } catch (e) {
      setError('PDF aanmaken mislukt: ' + (e?.message || e));
    } finally {
      setGenerating(false);
    }
  }, [getFresh, selectedId, week, agency]);

  const handleGenerateCombined = useCallback(async () => {
    setError('');
    setGenerating(true);
    try {
      await loadPdfLibs();
      const list = await getFresh();
      if (!list.length) { setError('Geen medewerkers met uren.'); return; }
      const doc = generateTotalListPdf(list.map((g) => ({ ...g.worker, rates: g.rates })), { weekNr: week, clientName: '', agency });
      doc.save(`Totaallijst ${profiel} week ${week} ${jaar}${labelSuffix}.pdf`);
    } catch (e) {
      setError('PDF aanmaken mislukt: ' + (e?.message || e));
    } finally {
      setGenerating(false);
    }
  }, [getFresh, week, jaar, profiel, agency, labelSuffix]);

  const handleGenerateAll = useCallback(async () => {
    setError('');
    setGenerating(true);
    try {
      await loadPdfLibs();
      const list = await getFresh();
      if (!list.length) { setError('Geen medewerkers met uren.'); return; }
      const zip = new window.JSZip();
      for (const item of list) {
        const doc = generateWorkerPdf(item.worker, { weekNr: week, clientName: '', agency, rates: item.rates });
        zip.file(`${safeFileName(item.worker.name)} uren wk ${week}.pdf`, doc.output('arraybuffer'));
        await new Promise((r) => setTimeout(r, 5));
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Urenlijsten ${agency.short} week ${week} ${jaar}${labelSuffix}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('ZIP aanmaken mislukt: ' + (e?.message || e));
    } finally {
      setGenerating(false);
    }
  }, [getFresh, week, jaar, profiel, agency, labelSuffix]);

  // ── Mail ──────────────────────────────────────────────────────────────────
  const openMailPerson = useCallback(async () => {
    setMailNote(null);
    const list = await getFresh();
    const item = list.find((g) => String(g.id) === String(selectedId)) || list[0];
    if (!item) { setError('Deze medewerker heeft geen uren.'); return; }
    setMail({
      mode: 'person',
      item,
      to: item.email || '',
      subject: buildSubject(item.worker.name),
      body: buildBody(item.worker.name),
    });
  }, [getFresh, selectedId, week, jaar, agency]);

  // Mail every worker-with-hours their own urenlijst, to their own e-mail.
  const openMailAll = useCallback(async () => {
    setMailNote(null);
    const list = await getFresh();
    if (!list.length) { setError('Geen medewerkers met uren.'); return; }
    const withEmail = list.filter((g) => g.email && g.email.trim());
    const without = list.filter((g) => !g.email || !g.email.trim());
    setMail({ mode: 'all', withEmail, without });
  }, [getFresh]);

  // overrides = { subject, body } — passed for the single-person send so the
  // (possibly edited) preview text is what actually goes out. Bulk uses defaults.
  const mailWorker = useCallback(async (g, overrides = {}) => {
    const doc = generateWorkerPdf(g.worker, { weekNr: week, clientName: '', agency, rates: g.rates });
    const pdfBase64 = pdfToBase64(doc);
    const filename = attachmentName(g.worker.name);
    const subject = overrides.subject != null ? overrides.subject : buildSubject(g.worker.name);
    const text = overrides.body != null ? overrides.body : buildBody(g.worker.name);
    const res = await authFetch('/api/uren/mail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        profiel, to: (g.email || '').trim(), subject, text, filename, pdfBase64,
        jaar, week, lijst_id: lijstId, worker_id: g.id, worker_name: g.worker.name,
      }),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'mislukt'); }
  }, [week, jaar, agency, profiel, lijstId]);

  const sendMail = useCallback(async () => {
    if (!mail || mailSending) return;
    setMailSending(true);
    setMailNote(null);
    try {
      await loadPdfLibs();
      if (mail.mode === 'person') {
        await mailWorker(
          { id: mail.item.id, worker: mail.item.worker, rates: mail.item.rates, email: mail.to },
          { subject: mail.subject, body: mail.body },
        );
        setMailNote({ ok: true, text: `Verstuurd naar ${mail.to.trim()}` });
      } else {
        // Bulk: each worker-with-email gets their own sheet.
        let sent = 0, failed = 0;
        for (const g of mail.withEmail) {
          try { await mailWorker(g); sent++; } catch { failed++; }
        }
        const parts = [`${sent} verstuurd`];
        if (failed) parts.push(`${failed} mislukt`);
        if (mail.without.length) parts.push(`${mail.without.length} zonder e-mail overgeslagen`);
        setMailNote({ ok: failed === 0, text: parts.join(' · ') });
      }
      setMail(null);
    } catch (e) {
      setMailNote({ ok: false, text: e.message });
    } finally {
      setMailSending(false);
    }
  }, [mail, mailSending, mailWorker]);

  // ── Mail history ────────────────────────────────────────────────────────────
  const loadHistory = useCallback(async (scope) => {
    setHistory({ loading: true, scope, entries: [] });
    try {
      const qs = new URLSearchParams({ profiel });
      if (scope === 'week') qs.set('lijst_id', lijstId);
      const res = await authFetch(`/api/uren/mail-log?${qs.toString()}`);
      if (!res.ok) throw new Error('load');
      const data = await res.json();
      setHistory({ loading: false, scope, entries: Array.isArray(data?.entries) ? data.entries : [] });
    } catch {
      setHistory({ loading: false, scope, entries: [], error: 'Kon geschiedenis niet laden.' });
    }
  }, [profiel, lijstId]);

  const fmtWhen = (ts) => {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts || '');
    return d.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="wgen-bar">
      <div className="wgen-left">
        <FileText size={15} className="wgen-icon" />
        <span className="wgen-count">
          {loading ? 'Laden…' : `${generatable.length} medewerker${generatable.length !== 1 ? 's' : ''} met uren`}
        </span>
      </div>

      <div className="wgen-right">
        <select
          className="wgen-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          disabled={loading || generating}
        >
          {generatable.length === 0 && <option value="">Nog geen uren</option>}
          {generatable.map((g) => (<option key={g.id} value={g.id}>{g.worker.name}</option>))}
        </select>

        <button className="wgen-btn wgen-btn-primary" onClick={handleGenerateOne} disabled={loading || generating}>
          {generating ? <Loader2 size={14} className="wgen-spin" /> : <FileText size={14} />}
          {generating ? 'Genereren…' : 'PDF'}
        </button>

        <button className="wgen-btn wgen-btn-secondary" onClick={openMailPerson} disabled={loading || generating}
          title="Deze medewerker de urenlijst mailen (PDF als bijlage)">
          <Mail size={14} /> Mail medewerker
        </button>

        <button className="wgen-btn wgen-btn-secondary" onClick={openMailAll} disabled={loading || generating}
          title="Elke medewerker z'n eigen urenlijst mailen naar z'n eigen e-mailadres">
          <Mail size={14} /> Mail alle medewerkers
        </button>

        <button className="wgen-btn wgen-btn-secondary" onClick={() => loadHistory('week')}
          title="Geschiedenis: welke urenlijsten zijn wanneer naar wie gemaild">
          <History size={14} /> Verzonden
        </button>

        <span className="wgen-sep" aria-hidden="true" />

        <button className="wgen-btn wgen-btn-secondary" onClick={handleGenerateCombined} disabled={loading || generating}
          title="Alle medewerkers in één totaallijst (zoals de originele weekurenlijst)">
          {generating ? <Loader2 size={14} className="wgen-spin" /> : <Files size={14} />}
          Totaallijst (PDF)
        </button>

        <button className="wgen-btn wgen-btn-secondary" onClick={handleGenerateAll} disabled={loading || generating}
          title="Elke medewerker als los PDF-bestand in een ZIP">
          {generating ? <Loader2 size={14} className="wgen-spin" /> : <Archive size={14} />}
          Urenlijst
        </button>
      </div>

      {error && <div className="wgen-error">{error}</div>}
      {mailNote && (
        <div className={`wgen-note ${mailNote.ok ? 'ok' : 'err'}`}>{mailNote.text}</div>
      )}

      {mail && (
        <div className="wgen-mail-backdrop" onMouseDown={() => !mailSending && setMail(null)}>
          <div className="wgen-mail" onMouseDown={(e) => e.stopPropagation()}>
            <div className="wgen-mail-head">
              <span>{mail.mode === 'person' ? `Urenlijst mailen — ${mail.item.worker.name}` : 'Alle medewerkers mailen'}</span>
              <button className="wgen-mail-x" onClick={() => setMail(null)} disabled={mailSending} aria-label="Sluiten"><X size={16} /></button>
            </div>

            {mail.mode === 'person' ? (
              <>
                <label className="wgen-mail-field">
                  <span>Naar</span>
                  <input
                    type="email"
                    value={mail.to}
                    onChange={(e) => setMail((m) => ({ ...m, to: e.target.value }))}
                    placeholder="ontvanger@email.nl"
                    autoFocus
                  />
                </label>
                {!mail.item.email && (
                  <p className="wgen-mail-hint">Deze medewerker heeft geen e-mailadres in de database — vul het hierboven in.</p>
                )}
                <label className="wgen-mail-field">
                  <span>Onderwerp</span>
                  <input type="text" value={mail.subject} onChange={(e) => setMail((m) => ({ ...m, subject: e.target.value }))} />
                </label>
                <label className="wgen-mail-field">
                  <span>Bericht</span>
                  <textarea
                    className="wgen-mail-textarea"
                    rows={7}
                    value={mail.body}
                    onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))}
                  />
                </label>
                <div className="wgen-mail-attach">
                  <Paperclip size={13} /> {attachmentName(mail.item.worker.name)}
                </div>
              </>
            ) : (
              <>
                <p className="wgen-mail-body">
                  <strong>{mail.withEmail.length}</strong> medewerker{mail.withEmail.length !== 1 ? 's' : ''} met e-mailadres krijgt de eigen urenlijst gemaild. Dit is wat elke medewerker ontvangt:
                </p>
                <div className="wgen-mail-field">
                  <span>Onderwerp (per medewerker)</span>
                  <div className="wgen-mail-preview">Urenlijst week {week} — <em>[naam]</em></div>
                </div>
                <div className="wgen-mail-field">
                  <span>Bericht</span>
                  <pre className="wgen-mail-preview wgen-mail-pre">{buildBody('[naam]')}</pre>
                </div>
                <div className="wgen-mail-field">
                  <span>Ontvangers ({mail.withEmail.length})</span>
                  <ul className="wgen-mail-reclist">
                    {mail.withEmail.map((g) => (
                      <li key={g.id}>
                        <span className="rn">{g.worker.name}</span>
                        <span className="re">{g.email}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="wgen-mail-attach">
                  <Paperclip size={13} /> <em>[naam]</em> uren wk {week}.pdf
                </div>
                {mail.without.length > 0 && (
                  <p className="wgen-mail-hint">
                    {mail.without.length} zonder e-mailadres worden overgeslagen: {mail.without.map((g) => g.worker.name).join(', ')}
                  </p>
                )}
              </>
            )}

            <p className="wgen-mail-from">Verstuurd vanaf <strong>{agency.email}</strong> · PDF automatisch als bijlage</p>

            <div className="wgen-mail-actions">
              <button className="wgen-btn wgen-btn-ghost" onClick={() => setMail(null)} disabled={mailSending}>Annuleren</button>
              <button
                className="wgen-btn wgen-btn-primary"
                onClick={sendMail}
                disabled={mailSending || (mail.mode === 'person' ? !mail.to.trim() : mail.withEmail.length === 0)}
              >
                {mailSending ? <Loader2 size={14} className="wgen-spin" /> : <Mail size={14} />}
                {mailSending ? 'Versturen…' : (mail.mode === 'all' ? `Versturen (${mail.withEmail.length})` : 'Versturen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {history && (
        <div className="wgen-mail-backdrop" onMouseDown={() => setHistory(null)}>
          <div className="wgen-mail wgen-hist" onMouseDown={(e) => e.stopPropagation()}>
            <div className="wgen-mail-head">
              <span>Verzonden urenlijsten</span>
              <button className="wgen-mail-x" onClick={() => setHistory(null)} aria-label="Sluiten"><X size={16} /></button>
            </div>

            <div className="wgen-hist-tabs">
              <button
                className={`wgen-hist-tab ${history.scope === 'week' ? 'active' : ''}`}
                onClick={() => loadHistory('week')}
              >Deze week</button>
              <button
                className={`wgen-hist-tab ${history.scope === 'all' ? 'active' : ''}`}
                onClick={() => loadHistory('all')}
              >Alle ({agency.short})</button>
            </div>

            {history.loading ? (
              <p className="wgen-mail-body"><Loader2 size={14} className="wgen-spin" /> Laden…</p>
            ) : history.error ? (
              <p className="wgen-mail-hint">{history.error}</p>
            ) : history.entries.length === 0 ? (
              <p className="wgen-mail-body">
                {history.scope === 'week'
                  ? `Nog niets gemaild voor week ${week} (${jaar}).`
                  : 'Nog geen verzonden urenlijsten.'}
              </p>
            ) : (
              <ul className="wgen-hist-list">
                {history.entries.map((e) => (
                  <li key={e.id} className={`wgen-hist-row ${e.status === 'failed' ? 'failed' : ''}`}>
                    <span className="wgen-hist-status" title={e.status === 'failed' ? (e.error || 'Mislukt') : 'Verstuurd'}>
                      {e.status === 'failed'
                        ? <AlertCircle size={15} className="ic-fail" />
                        : <CheckCircle2 size={15} className="ic-ok" />}
                    </span>
                    <div className="wgen-hist-main">
                      <div className="wgen-hist-line1">
                        <strong>{e.worker_name || '—'}</strong>
                        <span className="wgen-hist-to">{e.recipient}</span>
                      </div>
                      <div className="wgen-hist-line2">
                        {history.scope === 'all' && <span className="wgen-hist-week">wk {e.week}/{e.jaar}</span>}
                        <span className="wgen-hist-when">{fmtWhen(e.created_at)}</span>
                        {e.sent_by_name && <span className="wgen-hist-by">door {e.sent_by_name}</span>}
                      </div>
                      {e.status === 'failed' && e.error && <div className="wgen-hist-err">{e.error}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
